/**
 * The one save of a text's edit draft (ADR-115 Amendments 3 and 4): the PATCH body, the server's budget check for the card
 * marks while the save sheet is open, and the save with its landing on the text
 * screen (the flow save's toast, generation status and waiting words, §16.2).
 */
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { useLocale } from '../i18n/context'
import { useTextEditStore } from '../stores/textEditStore'
import { buildPayload, deckResolved, receivingDeck, type EditDraft } from '../utils/textEdit'
import { apiErrorCode, apiErrorDetail } from '../utils/textFlowErrors'
import type { TextBudgetCheck, TextDetail, TextFlowSaveResult, TextPreviewGap } from '../types'
import { useEditBudgetCheck, useUpdateText } from './useTexts'

interface Route {
  from?: string
  fromLabel?: string
}

export function useTextEditSave({ text, draft, gaps, sheetOpen, closeSheet, route }: {
  text: TextDetail
  draft: EditDraft
  /** The latest preview's gaps for the draft. */
  gaps: TextPreviewGap[]
  sheetOpen: boolean
  closeSheet: () => void
  route: Route
}) {
  const { tr } = useLocale()
  const te = tr.textEdit
  const navigate = useNavigate()
  const update = useUpdateText(text.id)
  const budgetCheck = useEditBudgetCheck(text.id)

  const payload = useMemo(() => buildPayload(text, draft, gaps), [text, draft, gaps])
  const hasCards = draft.cardMarks.length > 0
  const resolved = deckResolved(receivingDeck(draft))

  // ── The budget check, while the save sheet is open ─────────────────────────
  const [checked, setChecked] = useState<{ key: string; data: TextBudgetCheck | null } | null>(null)
  const checkKey = JSON.stringify([payload.card_marks, payload.card_destination, draft.deckIds, draft.dropWaiting])
  const wantsCheck = sheetOpen && hasCards && resolved
  useEffect(() => {
    if (!wantsCheck) return
    budgetCheck.mutate(payload, {
      onSuccess: (data) => setChecked({ key: checkKey, data }),
      onError: () => setChecked({ key: checkKey, data: null }),
    })
    // The key holds everything the check depends on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantsCheck, checkKey])
  const answer = wantsCheck && checked?.key === checkKey ? checked : null

  const save = () => {
    const store = useTextEditStore.getState()
    update.mutate(payload, {
      onSuccess: (result) => {
        store.reset()
        const cards = result.cards
        // With cards the text screen shows what the flow save's landing shows (§16.2):
        // the toast, the generation run and the waiting words.
        const saved: TextFlowSaveResult | undefined = cards ? {
          text: result, deck_id: cards.deck_id, text_import_id: cards.text_import_id,
          cards_accepted: cards.cards_accepted, skipped: cards.skipped, waiting: cards.waiting,
          not_created: [], budget_hit: cards.budget_hit, budget: cards.budget,
        } : undefined
        navigate(`/texts/${text.id}`, {
          replace: true,
          state: { from: route.from, fromLabel: route.fromLabel, ...(saved ? { saved, edited: true } : {}) },
        })
      },
      onError: (err) => {
        closeSheet()
        const code = apiErrorCode(err)
        if (code === 'TEXT_CARD_MARK_INVALID') {
          // Marks the server does not take as words of the body: drop them, say so.
          const bad = new Set(((apiErrorDetail(err)?.spans as [number, number][] | undefined) ?? []).map(([a]) => a))
          store.setCardMarks(draft.cardMarks.filter((m) => !bad.has(m.start)))
          toast.error(te.cardMarksInvalid)
        } else if (code === 'TEXT_CARDS_NEED_DECK') {
          toast.error(te.cardDeckNeeded)
        } else toast.error(te.saveFailed)
      },
    })
  }

  return {
    hasCards,
    /** Save waits only for a deck choice among several links. */
    blocked: hasCards && !resolved,
    check: answer?.data ?? null,
    checkFailed: answer !== null && answer.data === null,
    save,
    pending: update.isPending,
  }
}
